<template>
  <ContactReviewDisplayComponent
    :accountData="accountData"
    :companyIndex="companyIndex"
    :countryCode="'LT'"
    :editableAccountFields="accountFields"
    :editableCompanyFields="companyFields"
    :shouldShowCompanyFields="shouldShowCompanyFields"
    @updateAccountData="$emit('updateAccountData', $event)"
    @handleUpdateCompany="$emit('handleUpdateCompany', $event)"
    @selectExistingCompany="$emit('selectExistingCompany', $event)"
    @resetCompanySelection="$emit('resetCompanySelection')"
  />
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import type { Account, Company, ContactFieldType } from "@/types";
import { useValidators } from "@/composables/useValidators";
import { useI18n } from "vue-i18n";
import ContactReviewDisplayComponent from "@/signup/ContactReviewDisplayComponent.vue";

const props = withDefaults(
  defineProps<{
    accountData: Account;
    companyIndex: number;
    shouldShowCompanyFields?: boolean;
  }>(),
  { shouldShowCompanyFields: true }
);

const emit = defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
  selectExistingCompany: [companyId: number];
  resetCompanySelection: [];
}>();

const { t } = useI18n();
const {
  required,
  lithuanianCompanyNumber,
  lithuanianPersonalID,
  personalName,
  phoneNumber,
} = useValidators(t);

const accountFields = computed(() => {
  const fields: ContactFieldType[] = [
    {
      label: t("account.name"),
      id: "account-name",
      model: "name",
      type: "text",
      editable: true,
      validators: { required, personalName },
    },
  ];

  if (props.shouldShowCompanyFields) {
    // Company registration - show phone number
    fields.push({
      label: t("account.phone_number"),
      id: "account-phone-number",
      model: "phone_number",
      type: "tel",
      editable: true,
      validators: { required, phoneNumber },
    });
  } else {
    // Private individual - show personal ID
    fields.push(
      {
        label: t("account.registry_code"),
        id: "account-registry-code",
        model: "registry_code",
        type: "text",
        editable: true,
        validators: { required, lithuanianPersonalID },
      },
      {
        label: t("account.phone_number"),
        id: "account-phone-number",
        model: "phone_number",
        type: "tel",
        editable: true,
        validators: { required, phoneNumber },
      }
    );
  }

  return fields;
});

const companyFields = computed(() => {
  const fields: ContactFieldType[] = [];

  if (props.shouldShowCompanyFields) {
    // Company view - show all fields
    fields.push(
      {
        label: t("company.name"),
        id: "company-name",
        model: "name",
        type: "text",
        editable: true,
        validators: { required },
      },
      {
        label: t("company.registry_code"),
        id: "company-registry-code",
        model: "registry_code",
        type: "text",
        editable: true,
        validators: [required, lithuanianCompanyNumber],
      }
    );
  }

  // Always show address
  fields.push({
    label: t("company.address"),
    id: "company-address",
    model: "address",
    type: "text",
    editable: true,
    validators: { required },
  });

  return fields;
});

// A private individual is their own company, so the company's name and registry code
// track the account holder's. The mode is watched alongside the value, and re-checked on
// every run, because the LT flow flips it on a live instance: the user type lives in a
// query param and LTSignupFlow's `:key="companyIndex"` does not change with it. Reading
// the mode only at setup stranded the mirroring in whichever direction the component
// happened to mount in.
watch(
  [() => props.shouldShowCompanyFields, () => props.accountData.name],
  ([showsCompanyFields, newName]) => {
    if (!showsCompanyFields && newName && props.companyIndex !== -1) {
      emit("handleUpdateCompany", {
        ...props.accountData.companies[props.companyIndex],
        name: newName,
      });
    }
  },
  { immediate: true }
);

watch(
  [() => props.shouldShowCompanyFields, () => props.accountData.registry_code],
  ([showsCompanyFields, newCode]) => {
    if (!showsCompanyFields && newCode && props.companyIndex !== -1) {
      emit("handleUpdateCompany", {
        ...props.accountData.companies[props.companyIndex],
        registry_code: newCode,
      });
    }
  },
  { immediate: true }
);
</script>
